import './ProjectDetailsDatasetsTable.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBars, faEye, faTriangleExclamation, faUnlink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { compact } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useHistory } from 'react-router';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { WarningIconBadge } from '../../../components/badges/iconBadges/WarningIconBadge';
import { GTable } from '../../../components/table/GTable';
import { IGColumnProps } from '../../../components/table/IGColumnProps';
import { EnableFor } from '../../../containers/EnableForRole';
import { Home } from '../../../routes/config/Home';
import { IProjectDataset } from '../projectDetails.models';
import { useProjectDetailsPermissions } from '../projectDetails.permissions';

library.add(faUnlink, faEye);

interface IProjectDetailsDatasetsTableProps {
  workspaceId: string;
  projectDatasets: IProjectDataset[];
  isLoading: boolean;
  onUnlink: (id: string) => void;
  onDatasetOrderChange: (from: number, to: number) => Promise<void>;
}

export const ProjectDetailsDatasetsTable = observer((props: IProjectDetailsDatasetsTableProps): ReactElement => {
  const { t } = useTranslator();
  const history = useHistory();

  const permissions = useProjectDetailsPermissions();
  const canViewDataset = permissions.canViewDataset();
  const canUnlinkDataset = permissions.canUnlinkDataset();
  const canEditDatasetOrder = permissions.canEditDatasetOrder();

  const { workspaceId, projectDatasets, isLoading, onUnlink, onDatasetOrderChange } = props;

  const renderName = (projectDataset: IProjectDataset): ReactElement => {
    const isDraft = projectDataset.status === 'Draft';
    return isDraft ? renderNameDraft(projectDataset) : renderNamePublished(projectDataset);
  };

  const renderNameDraft = (projectDataset: IProjectDataset): ReactElement => (
    <RowLayout size='md'>
      <S_Link2
        color='text'
        to={Home.Datasets.Details.Images.withParams({ datasetId: projectDataset.id, workspaceId })}
      >
        {projectDataset.name}
      </S_Link2>
      <S_Link2
        color='text'
        to={Home.Datasets.Details.Images.withParams({ datasetId: projectDataset.id, workspaceId })}
      >
        <WarningIconBadge title={t('dataset_must_be_published_warning')} />
      </S_Link2>
    </RowLayout>
  );

  const renderNamePublished = (projectDataset: IProjectDataset): ReactElement => (
    <S_Link2
      color='text'
      to={Home.Datasets.Details.Images.withParams({ datasetId: projectDataset.id, workspaceId })}
    >
      {projectDataset.name}
    </S_Link2>
  );

  const renderDescription = (projectDataset: IProjectDataset): ReactElement => {
    const description = projectDataset.description;
    return <span title={description}>{description}</span>;
  };

  const renderReviewsDonePercentage = (projectDataset: IProjectDataset): ReactElement => <>{`${projectDataset.reviewsDonePercentage}%`}</>;

  const renderActions = (projectDataset: IProjectDataset): ReactElement => (
    <>
      <EnableFor hasPermission={canViewDataset}>
        <IconButton
          size='sm'
          color='primary'
          title={t('view_dataset')}
          onClick={() => {
            history.push(Home.Datasets.Details.Images.withParams({ datasetId: projectDataset.id, workspaceId }));
          }}
        >
          <FontAwesomeIcon icon={faEye} />
        </IconButton>
      </EnableFor>
      <EnableFor hasPermission={canUnlinkDataset}>
        <IconButton
          size='sm'
          onClick={(): void => {
            onUnlink(projectDataset.id);
          }}
          title={t('unlink_dataset')}
        >
          <FontAwesomeIcon icon={faUnlink} />
        </IconButton>
      </EnableFor>
    </>
  );

  const renderSize = (item: IProjectDataset): ReactElement => {
    return (
      <RowLayout size='xs'>
        {(item.imageSizeInB / (1024 * 1024)).toFixed(2)}
        {item.imageSizeInB >= 1024 * 1024 * 1024 && (
          <FontAwesomeIcon
            title={t('dataset_size_limit_tooltip')}
            className='warning-icon'
            icon={faTriangleExclamation}
          />
        )}
      </RowLayout>
    );
  };

  const gColumns = (): IGColumnProps<IProjectDataset>[] => {
    return compact<IGColumnProps<IProjectDataset>>([
      canEditDatasetOrder.allowed && {
        headerName: '',
        isDragHandle: true,
        renderer: () => <FontAwesomeIcon icon={faBars} />,
      },
      {
        headerName: t('name'),
        renderer: renderName,
      },
      {
        headerName: t('description'),
        renderer: renderDescription,
      },
      {
        headerName: t('images'),
        renderer: (x) => x.imagesCount,
      },
      {
        headerName: t('percent_done'),
        renderer: renderReviewsDonePercentage,
      },
      {
        headerName: t('size_of_images'),
        renderer: renderSize,
      },
      {
        headerName: t('actions'),
        renderer: renderActions,
        actions: true,
        width: 'min-content',
      },
    ]);
  };

  return (
    <GTable<IProjectDataset>
      isLoading={isLoading}
      noItemsPlaceholder={t('no_datasets_have_been_assigned_yet')}
      columns={gColumns()}
      items={projectDatasets}
      enableDrag={true}
      className='project-details-datasets-table'
      onRowDragEnd={onDatasetOrderChange}
    />
  );
});
